import { useEffect, useRef, useState } from 'react';
import '../styles/ProjectPage.css'
import { initGalaxy } from '../scripts/threejs-galaxy.js';

const DEFAULT_PARAMS = {
    count: 180000,
    size: 0.01,
    radius: 5,
    branches: 5,
    spin: 1,
    randomness: 0.35,
    randomnessPower: 4,
    insideColor: '#ff5a00',
    outsideColor: '#1b3984'
}

function Slider({ label, name, min, max, step, value, onChange, onCommit, display }) {
    return (
        <div className="gc-row">
            <span className="gc-label">{label}</span>
            <input
                className="gc-range"
                type="range"
                min={min} max={max} step={step}
                value={value}
                onChange={e => onChange(name, step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value))}
                onPointerUp={e => onCommit(name, step < 1 ? parseFloat(e.target.value) : parseInt(e.target.value))}
            />
            <span className="gc-value">{display ?? value}</span>
        </div>
    )
}

export default function ProjectThreeGalaxy() {
    const canvasRef = useRef(null);
    const galaxyRef = useRef(null);
    const [params, setParams] = useState(DEFAULT_PARAMS);
    const [open, setOpen] = useState(false);

    useEffect(() => {
        window.scrollTo(0, 0);
        if (canvasRef.current) {
            galaxyRef.current = initGalaxy(canvasRef.current);
        }
        return () => { galaxyRef.current?.cleanup() };
    }, []);

    const handleChange = (key, value) => {
        setParams(prev => ({ ...prev, [key]: value }));
    };

    const handleCommit = (key, value) => {
        galaxyRef.current?.updateParams({ [key]: value });
        setParams(prev => ({ ...prev, [key]: value }));
    };

    const handleColor = (key, value) => {
        galaxyRef.current?.updateParams({ [key]: value });
        setParams(prev => ({ ...prev, [key]: value }));
    };

    return (
        <div className="project-page">
            <main className="project-main">
                <div className="webgl-wrapper">
                    <canvas ref={canvasRef} className="webgl"></canvas>
                </div>

                <div className="project-content">
                    <h1>Galaxy Generator · Projet 3D interactif avec Three.js</h1>
                    <p className="project-desc">
                        Un générateur de galaxie procédural entièrement paramétrable en temps réel.
                        Nombre de particules, branches, spin, couleurs — chaque paramètre
                        modifie la galaxie instantanément via le panneau de contrôle. <br />
                        Réalisé dans le cadre de la formation "Three.js Journey" de Bruno Simon.
                    </p>

                    <div className="project-tech">
                        <h3>Techno</h3>
                        <ul>
                            <li>Three.js (WebGL)</li>
                            <li>BufferGeometry & BufferAttribute (180 000 particules)</li>
                            <li>PointsMaterial avec alphaMap et AdditiveBlending</li>
                            <li>Génération procédurale (branches, spin, randomness)</li>
                            <li>Contrôles temps réel (React)</li>
                        </ul>
                    </div>

                    <div className="galaxy-controls">
                        <button
                            className="gc-toggle"
                            onClick={() => setOpen(o => !o)}
                            aria-expanded={open}
                        >
                            <span>Personnaliser</span>
                            <span className={`gc-chevron ${open ? 'gc-chevron--open' : ''}`}>›</span>
                        </button>

                        {open && (
                            <div className="gc-body">
                                <Slider label="Particules" name="count"
                                    min={1000} max={500000} step={1000}
                                    value={params.count}
                                    display={params.count.toLocaleString('fr-FR')}
                                    onChange={handleChange} onCommit={handleCommit} />
                                <Slider label="Taille" name="size"
                                    min={0.001} max={0.05} step={0.001}
                                    value={params.size}
                                    onChange={handleChange} onCommit={handleCommit} />
                                <Slider label="Rayon" name="radius"
                                    min={0.5} max={20} step={0.1}
                                    value={params.radius}
                                    onChange={handleChange} onCommit={handleCommit} />
                                <Slider label="Branches" name="branches"
                                    min={1} max={10} step={1}
                                    value={params.branches}
                                    onChange={handleChange} onCommit={handleCommit} />
                                <Slider label="Spin" name="spin"
                                    min={-5} max={5} step={0.01}
                                    value={params.spin}
                                    onChange={handleChange} onCommit={handleCommit} />
                                <Slider label="Dispersion" name="randomness"
                                    min={0} max={2} step={0.01}
                                    value={params.randomness}
                                    onChange={handleChange} onCommit={handleCommit} />
                                <Slider label="Relief" name="randomnessPower"
                                    min={1} max={10} step={0.1}
                                    value={params.randomnessPower}
                                    onChange={handleChange} onCommit={handleCommit} />

                                <div className="gc-row gc-row--color">
                                    <span className="gc-label">Couleur cœur</span>
                                    <label className="gc-color-wrap" style={{ '--swatch': params.insideColor }}>
                                        <input type="color"
                                            value={params.insideColor}
                                            onChange={e => handleColor('insideColor', e.target.value)} />
                                    </label>
                                    <span className="gc-value">{params.insideColor}</span>
                                </div>
                                <div className="gc-row gc-row--color">
                                    <span className="gc-label">Couleur bord</span>
                                    <label className="gc-color-wrap" style={{ '--swatch': params.outsideColor }}>
                                        <input type="color"
                                            value={params.outsideColor}
                                            onChange={e => handleColor('outsideColor', e.target.value)} />
                                    </label>
                                    <span className="gc-value">{params.outsideColor}</span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>
        </div>
    );
}
